// Preload wrapper — delegates to paginated MediaFeed.
import { cacheKey, getCachedMedia, putCachedMedia } from '../cache/db'
import { MediaFeed } from './feed'
import type { ContentKind, MediaItem } from '../types'

export interface PreloadTag {
  id: string
  label: string
  query: string
}

export interface PreloadProgress {
  loadedMs: number
  targetMs: number
  count: number
  fraction: number
  note?: string
}

export interface PreloadOpts {
  tags: PreloadTag[]
  kind: ContentKind
  targetMs?: number
  /** Minimum tiles before returning (browse modes). */
  minItems?: number
  onProgress?: (p: PreloadProgress) => void
  signal?: AbortSignal
}

export const TEN_MIN_MS = 10 * 60_000
export const SIX_MIN_MS = 6 * 60_000

export function targetFor(kind: ContentKind): number {
  return kind === 'both' ? TEN_MIN_MS : SIX_MIN_MS
}

function sumWorth(items: MediaItem[]): number {
  return items.reduce((n, m) => n + m.worthMs, 0)
}

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function warm(items: MediaItem[], n = 8): void {
  for (const m of items.slice(0, n)) {
    if (m.kind === 'image' && m.src) {
      const img = new Image()
      img.decoding = 'async'
      img.src = m.src
    }
  }
}

export async function preloadTags(opts: PreloadOpts): Promise<MediaItem[]> {
  const minItems = opts.minItems ?? 24
  const target = opts.targetMs ?? minItems * 4000

  const key = cacheKey(opts.tags.map((t) => t.id), opts.kind)
  const cached = await getCachedMedia(key)
  if (cached && cached.length >= Math.min(minItems, 12)) {
    opts.onProgress?.({
      loadedMs: sumWorth(cached),
      targetMs: target,
      count: cached.length,
      fraction: 1,
    })
    const out = shuffle(cached)
    warm(out)
    return out
  }

  const feed = new MediaFeed({
    tags: opts.tags,
    kind: opts.kind,
    onProgress: opts.onProgress,
    signal: opts.signal,
  })

  await feed.loadUntil(minItems, target)
  const out = shuffle(feed.queue)
  warm(out)
  if (out.length) await putCachedMedia(key, out)
  return out
}